// Photo capture — wraps the browser MediaDevices API
// Uses <input type="file" capture="environment"> for maximum iOS/Android compatibility.
// Returns a local object URL and stores the blob in OPFS.

import { v4 as uuidv4 } from 'uuid';
import type { Photo } from './world-model';

async function writeBlobToOPFS(key: string, blob: Blob): Promise<void> {
  const root = await navigator.storage.getDirectory();
  const fileHandle = await root.getFileHandle(key, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

async function readBlobFromOPFS(key: string): Promise<Blob | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(key);
    return handle.getFile();
  } catch {
    return null;
  }
}

/**
 * Opens the camera (or file picker) and returns a Photo record
 * with the binary stored in OPFS and a local object URL for display.
 */
export async function capturePhoto(lat?: number, lng?: number): Promise<Photo | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // rear camera on mobile

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) { resolve(null); return; }

      const id = uuidv4();
      const opfsKey = `photo-${id}`;

      try {
        await writeBlobToOPFS(opfsKey, file);
        const localUri = URL.createObjectURL(file);

        // Generate a thumbnail for the catch list
        const thumbnailUri = await generateThumbnail(file);

        resolve({
          id,
          localUri,
          thumbnailUri: thumbnailUri ?? undefined,
          capturedAt: new Date().toISOString(),
          lat,
          lng,
        });
      } catch (err) {
        console.error('Photo capture error:', err);
        resolve(null);
      }
    };

    input.click();
  });
}

async function generateThumbnail(file: File, maxSize = 200): Promise<string | null> {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export async function getPhotoBlob(opfsKey: string): Promise<Blob | null> {
  return readBlobFromOPFS(opfsKey);
}
